import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon, XIcon } from '../components/icons';
import { Button, Input, Loading, Spinner, cx } from '../components/ui';
import { timecode } from '../lib/format';
import { MIN_CLIP_SEC, plannedDuration, toSource, type Placed } from '../lib/timeline';
import type { VideoSummary } from '../lib/types';
import type { Clip, ClipSource } from '../queries/edit';
import { qk } from '../queries/keys';
import { fetchLibrary } from '../queries/videos';

/**
 * Stitching: pieces of other recordings, dropped into this one.
 *
 * The editor's main track stays what it always was, the original recording
 * with the cuts drawn on it, because the transcript, the waveform and every
 * selection are about the original and would start lying if that track
 * became the finished video. The stitching lives on a second strip below it:
 * the finished video, piece by piece, where a clip can be dragged to a
 * different moment and trimmed at its edges.
 *
 * The maths behind both is in lib/timeline.ts.
 */

// ---------------------------------------------------------------------------
// Picking a recording to add.

/**
 * The same search as the intro and outro pickers on the branding page, and
 * for the same reason: the search runs on the API rather than over what was
 * already fetched, so a library of three hundred recordings is all of it,
 * and the transcript is searched too.
 */
export function ClipPicker({
  excludeVideoId,
  onPick,
  onClose,
}: {
  excludeVideoId: string;
  onPick: (video: VideoSummary) => void;
  onClose: () => void;
}) {
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const library = useQuery({
    queryKey: qk.videos(`stitch:${query}`),
    queryFn: () => fetchLibrary({ limit: 100, q: query || undefined }),
    staleTime: 30_000,
  });
  // A clip has to be something that will actually play where it is dropped:
  // this recording itself, a voice note, one still processing or one behind
  // the free-tier window would each be a hole in the middle of the finished
  // video.
  const choices = (library.data?.videos ?? []).filter(
    (video: VideoSummary) =>
      video.id !== excludeVideoId &&
      video.status === 'ready' &&
      video.kind !== 'audio' &&
      !video.locked &&
      !video.deletedAt &&
      Boolean(video.durationSec),
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/70 p-6 pt-20"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label="Add a clip"
        className="w-full max-w-lg rounded-lg border border-hairline bg-slate-900 p-4 shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-h4 text-chalk">Add a clip</h2>
            <p className="mt-0.5 text-caption text-chalk-faint">
              A piece of another recording, dropped into this one. The one you pick is not touched.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-chalk-dim hover:text-chalk"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="relative mt-3">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-chalk-faint">
            <SearchIcon size={14} />
          </span>
          <Input
            type="search"
            className="pl-8"
            placeholder="Search your recordings"
            value={term}
            autoFocus
            spellCheck={false}
            onChange={(event) => setTerm(event.target.value)}
            aria-label="Search recordings to add as a clip"
          />
        </div>

        {library.isPending ? (
          <div className="mt-3">
            <Loading label="Loading your recordings" />
          </div>
        ) : choices.length === 0 ? (
          <p className="mt-4 text-caption text-chalk-faint">
            {query
              ? `Nothing matches “${query}”.`
              : 'Nothing else to add yet. Record the piece first, then come back and drop it in.'}
          </p>
        ) : (
          <ul className="mt-3 max-h-80 space-y-1 overflow-y-auto pr-1">
            {choices.map((video) => (
              <li key={video.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded p-1.5 text-left hover:bg-slate-800"
                  onClick={() => onPick(video)}
                >
                  <span className="h-10 w-16 shrink-0 overflow-hidden rounded bg-slate-950">
                    {video.posterUrl ? (
                      <img src={video.posterUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body text-chalk">{video.title}</span>
                    {video.durationSec ? (
                      <span className="text-caption tnum font-mono text-chalk-faint">
                        {timecode(video.durationSec)}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The composition strip: the finished video, piece by piece.

type StripDrag =
  | { kind: 'move'; clipId: string; grabOutOffset: number }
  | { kind: 'trim'; clipId: string; edge: 'start' | 'end' }
  | null;

export function CompositionStrip({
  plan,
  sources,
  selectedClipId,
  outTime,
  onSelectClip,
  onMoveClip,
  onTrimClip,
  onRemoveClip,
  onSeekSource,
  onAdd,
  addDisabled,
  duration,
}: {
  plan: Placed[];
  sources: Map<string, ClipSource>;
  selectedClipId: string | null;
  /** The playhead, in the finished video. Null when it sits in a cut. */
  outTime: number | null;
  onSelectClip: (clipId: string | null) => void;
  onMoveClip: (clipId: string, at: number) => void;
  onTrimClip: (clipId: string, edge: 'start' | 'end', to: number) => void;
  onRemoveClip: (clipId: string) => void;
  /** Clicking a piece of the recording puts the playhead there. */
  onSeekSource: (at: number) => void;
  onAdd: () => void;
  addDisabled: boolean;
  duration: number;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const drag = useRef<StripDrag>(null);
  const total = Math.max(plannedDuration(plan), 0.001);
  const clips = plan.filter((piece) => piece.kind === 'clip');

  const outAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.max(0, Math.min(total, ((clientX - rect.left) / rect.width) * total));
  };
  const left = (out: number) => `${(out / total) * 100}%`;
  const width = (seconds: number) => `${(seconds / total) * 100}%`;

  /**
   * Where a drag wants the clip. The plan is recomputed without the clip
   * being moved, so the moment under the cursor does not slide about as the
   * clip's own width gets out of the way ahead of it.
   */
  const dropAt = (state: Extract<StripDrag, { kind: 'move' }>, clientX: number): number => {
    const moving = plan.find((piece) => piece.clip?.id === state.clipId);
    if (!moving) return 0;
    const others = plan.filter((piece) => piece.clip?.id !== state.clipId);
    let out = 0;
    const without: Placed[] = others.map((piece) => {
      const placed = { ...piece, outStart: out };
      out += piece.end - piece.start;
      return placed;
    });
    const wanted = Math.max(0, outAt(clientX) - state.grabOutOffset);
    return toSource(Math.min(wanted, out), without, duration);
  };

  useEffect(() => {
    const move = (event: MouseEvent) => {
      const state = drag.current;
      if (!state) return;
      event.preventDefault();
      if (state.kind === 'move') onMoveClip(state.clipId, dropAt(state, event.clientX));
      else {
        const piece = plan.find((one) => one.clip?.id === state.clipId);
        if (!piece?.clip) return;
        // A trim handle moves in the finished video's seconds, and a second
        // there is a second inside the source: same scale, no conversion.
        const delta =
          outAt(event.clientX) -
          (state.edge === 'start' ? piece.outStart : piece.outStart + (piece.end - piece.start));
        onTrimClip(
          state.clipId,
          state.edge,
          (state.edge === 'start' ? piece.clip.start : piece.clip.end) + delta,
        );
      }
    };
    // Nothing to commit on release: one drag is one undo, and the editor
    // remembers where it started when the first move comes in.
    const up = () => {
      drag.current = null;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  });

  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-center gap-4 text-caption">
        <button
          type="button"
          onClick={onAdd}
          disabled={addDisabled}
          className="flex items-center gap-1.5 text-chalk-dim disabled:opacity-40 hover:text-chalk"
        >
          <PlusIcon size={13} />
          Add clip
        </button>
        <span className="flex-1" />
        <span className="text-chalk-faint">
          {clips.length === 0
            ? 'The finished video. Add a clip to drop another recording into it.'
            : `${clips.length} clip${clips.length === 1 ? '' : 's'} · drag one along the strip to move it · drag its edges to trim`}
        </span>
      </div>

      <div
        ref={trackRef}
        className="relative h-12 select-none overflow-hidden rounded-md border border-hairline bg-slate-900"
      >
        {plan.map((piece, index) => {
          const seconds = piece.end - piece.start;
          if (piece.kind === 'self') {
            return (
              <button
                key={`self-${index}-${piece.outStart}`}
                type="button"
                title={`${timecode(piece.start)} to ${timecode(piece.end)} of this recording`}
                onClick={() => {
                  onSelectClip(null);
                  onSeekSource(piece.start);
                }}
                className="absolute inset-y-0 border-r border-slate-950/60 bg-slate-800/70 hover:bg-slate-800"
                style={{ left: left(piece.outStart), width: width(seconds) }}
              >
                <span className="pointer-events-none block truncate px-2 text-left text-micro text-chalk-faint">
                  {seconds > 2 ? 'This recording' : ''}
                </span>
              </button>
            );
          }
          const clip = piece.clip;
          if (!clip) return null;
          const source = sources.get(clip.videoId);
          const selected = clip.id === selectedClipId;
          return (
            <div
              key={clip.id}
              className={cx(
                'group/clip absolute inset-y-0 overflow-hidden border-x border-slate-950/60 bg-signal/25',
                selected && 'ring-1 ring-inset ring-signal',
                !source?.available && 'bg-caution/25',
              )}
              style={{ left: left(piece.outStart), width: width(seconds) }}
            >
              <button
                type="button"
                aria-label={`Move ${source?.title ?? 'clip'}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onSelectClip(clip.id);
                  drag.current = {
                    kind: 'move',
                    clipId: clip.id,
                    grabOutOffset: outAt(event.clientX) - piece.outStart,
                  };
                }}
                className="absolute inset-0 cursor-grab px-2 text-left active:cursor-grabbing"
                style={
                  source?.posterUrl
                    ? {
                        backgroundImage: `url(${source.posterUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : undefined
                }
              >
                <span className="absolute inset-0 bg-slate-950/55" />
                <span className="relative block truncate pt-1 text-micro text-chalk">
                  {source?.available === false ? 'Unavailable' : (source?.title ?? 'Clip')}
                </span>
                <span className="relative block truncate tnum font-mono text-micro text-chalk-dim">
                  {timecode(seconds)}
                </span>
              </button>
              <div
                role="separator"
                aria-label="Trim the start of this clip"
                aria-orientation="vertical"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onSelectClip(clip.id);
                  drag.current = { kind: 'trim', clipId: clip.id, edge: 'start' };
                }}
                className="absolute inset-y-0 left-0 w-1.5 cursor-col-resize bg-signal/80 hover:bg-signal"
              />
              <div
                role="separator"
                aria-label="Trim the end of this clip"
                aria-orientation="vertical"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onSelectClip(clip.id);
                  drag.current = { kind: 'trim', clipId: clip.id, edge: 'end' };
                }}
                className="absolute inset-y-0 right-0 w-1.5 cursor-col-resize bg-signal/80 hover:bg-signal"
              />
              <button
                type="button"
                aria-label={`Remove ${source?.title ?? 'this clip'}`}
                title="Remove this clip"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => onRemoveClip(clip.id)}
                className={cx(
                  'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full',
                  'border border-danger/60 bg-slate-950/85 text-danger transition-colors duration-120',
                  'hover:border-danger hover:bg-danger hover:text-white',
                  // Always there on the clip being worked on, and on hover
                  // for the rest: a control that removes something should
                  // not have to be hunted for.
                  selected
                    ? 'opacity-100'
                    : 'opacity-0 focus-visible:opacity-100 group-hover/clip:opacity-100',
                )}
              >
                <XIcon size={12} />
              </button>
            </div>
          );
        })}
        {outTime !== null ? (
          <div
            className="pointer-events-none absolute inset-y-0 z-20 w-px bg-chalk"
            style={{ left: left(outTime) }}
          />
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trimming the selected clip, with the clip in front of you.

/**
 * A clip's own length, with the kept part lit and the rest dimmed, and the
 * clip itself above it to scrub.
 *
 * Trimming is possible from the strip alone, but not usefully: the strip
 * shows the clip as a sliver of the finished video and nobody can pick the
 * moment a sentence starts out of eight pixels. This is where the in and out
 * points are actually chosen.
 */
export function ClipTrimmer({
  clip,
  source,
  onTrim,
  onRemove,
  onClose,
}: {
  clip: Clip;
  source: ClipSource | undefined;
  onTrim: (edge: 'start' | 'end', to: number, committing?: boolean) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const drag = useRef<'start' | 'end' | null>(null);
  const [at, setAt] = useState(clip.start);
  const [playing, setPlaying] = useState(false);
  const length = source?.durationSec ?? Math.max(clip.end, 1);

  // Playing the clip means playing the kept part of it: run off the out
  // point and it stops, the way it will in the finished video.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      setAt(video.currentTime);
      if (!video.paused && video.currentTime >= clip.end) video.pause();
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [clip.end]);

  const timeAt = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.max(0, Math.min(length, ((clientX - rect.left) / rect.width) * length));
  };
  const seek = (to: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(to, length));
    setAt(video.currentTime);
  };

  useEffect(() => {
    const move = (event: MouseEvent) => {
      const edge = drag.current;
      if (!edge) return;
      event.preventDefault();
      onTrim(edge, timeAt(event.clientX));
    };
    const up = () => {
      drag.current = null;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  });

  const left = (time: number) => `${(time / length) * 100}%`;

  return (
    <div className="mt-3 flex gap-3 rounded-md border border-hairline bg-slate-900/60 p-3">
      <div className="w-56 shrink-0 overflow-hidden rounded bg-black">
        {source?.url ? (
          <video
            ref={videoRef}
            src={source.url}
            poster={source.posterUrl ?? undefined}
            preload="metadata"
            playsInline
            className="aspect-video w-full"
            onLoadedMetadata={() => seek(clip.start)}
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              if (video.paused) {
                if (video.currentTime >= clip.end - 0.05) seek(clip.start);
                void video.play().catch(() => {});
              } else video.pause();
            }}
          />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center text-caption text-chalk-faint">
            {source ? 'Cannot be played' : <Spinner size={16} />}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 truncate text-body text-chalk">{source?.title ?? 'Clip'}</p>
          <span className="tnum font-mono text-caption text-chalk-faint">
            {timecode(clip.end - clip.start)} of {timecode(length)}
          </span>
          <Button size="sm" variant="danger" onClick={onRemove}>
            <TrashIcon size={13} />
            Remove clip
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="text-caption text-chalk-dim hover:text-chalk"
          >
            Done
          </button>
        </div>

        <div
          ref={barRef}
          className="relative mt-2 h-9 cursor-pointer overflow-hidden rounded border border-hairline bg-slate-950"
          onMouseDown={(event) => seek(timeAt(event.clientX))}
        >
          <div
            className="absolute inset-y-0 bg-signal/25"
            style={{
              left: left(clip.start),
              width: `${((clip.end - clip.start) / length) * 100}%`,
            }}
          />
          {(['start', 'end'] as const).map((edge) => (
            <div
              key={edge}
              role="separator"
              aria-orientation="vertical"
              aria-label={edge === 'start' ? 'In point' : 'Out point'}
              onMouseDown={(event) => {
                event.stopPropagation();
                event.preventDefault();
                drag.current = edge;
              }}
              className="absolute inset-y-0 z-10 w-2 -translate-x-1/2 cursor-col-resize bg-signal"
              style={{ left: left(edge === 'start' ? clip.start : clip.end) }}
            />
          ))}
          <div
            className="pointer-events-none absolute inset-y-0 z-20 w-px bg-chalk"
            style={{ left: left(at) }}
          />
        </div>

        <div className="mt-2 flex items-center gap-3 text-caption">
          <button
            type="button"
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              if (video.paused) {
                if (video.currentTime >= clip.end - 0.05) seek(clip.start);
                void video.play().catch(() => {});
              } else video.pause();
            }}
            className="text-chalk-dim hover:text-chalk"
          >
            {playing ? 'Pause' : 'Play the clip'}
          </button>
          <Button
            size="sm"
            onClick={() => onTrim('start', at, true)}
            disabled={at >= clip.end - MIN_CLIP_SEC}
          >
            In here
          </Button>
          <Button
            size="sm"
            onClick={() => onTrim('end', at, true)}
            disabled={at <= clip.start + MIN_CLIP_SEC}
          >
            Out here
          </Button>
          <span className="tnum font-mono text-chalk-faint">
            {timecode(clip.start)} to {timecode(clip.end)}
          </span>
        </div>
      </div>
    </div>
  );
}
